import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, Button, Chip, useTheme, useMediaQuery, Alert, CircularProgress, Snackbar, Skeleton } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import judgeIcon from '../assets/icons/iconsax-judge.svg';
import documentIcon from '../assets/icons/iconsax-document-text-2.svg';
import shieldSearchIcon from '../assets/icons/iconsax-shield-search.svg';
import Sidebar from '../components/Sidebar';
import DocumentHistory from '../components/DocumentHistory';
import {
  Chat,
  Description,
  FindInPage,
  Search,
  History,
  TrendingUp,
  AccessTime,
  CheckCircle,
  ArrowForward,
  Person,
  Settings,
  Logout
} from '@mui/icons-material';
import { api } from '../services/api';
import AuthModal from '../components/auth/AuthModal';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { collapsed } = useSidebar();
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  // Recent generated documents for "Nedávná aktivita"
  interface DocumentHistoryItem {
    filename: string;
    file_size: number;
    generated_at: string;
    download_url: string;
    contract_type: string;
  }

  const [recentDocs, setRecentDocs] = useState<DocumentHistoryItem[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState('');

  // Welcome toast after login/registration
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [welcomeName, setWelcomeName] = useState<string | null>(null);
  // Auth modal state (opens automatically when redirected for auth)
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login');

  useEffect(() => {
    const state = (location && (location as any).state) as any;
    if (state && state.welcomeUser) {
      setWelcomeName(state.welcomeUser);
      setWelcomeOpen(true);
      navigate(location.pathname, { replace: true });
    }

    // Dashboard can be reached in multiple ways. Check both router state and URL params
    const params = new URLSearchParams(location.search);
    const requireRegistrationParam = params.get('requireRegistration');
    const authModeParam = params.get('authMode');
    const intendedPathParam = params.get('intendedPath');

    // If redirected for registration and user is not authenticated, open auth modal directly
    if (!isAuthenticated && (state?.requireRegistration || requireRegistrationParam === '1')) {
      if (intendedPathParam) {
        try { sessionStorage.setItem('intendedPath', decodeURIComponent(intendedPathParam)); } catch {}
      }
      // Determine which mode to open: prefer explicit state/authMode param, default to register for CTA flow
      const modeFromState = state?.authMode;
      const modeFromQuery = authModeParam;
      const mode: 'login' | 'register' = (modeFromState === 'register' || modeFromQuery === 'register') ? 'register' : 'login';
      setAuthModalMode(mode);
      setAuthModalOpen(true);
      // Clean the query string so modal doesn't reopen on refresh
      if (requireRegistrationParam) {
        navigate(location.pathname, { replace: true });
      }
    }

    // Listen for global event from axios interceptor to open auth modal
    const openAuthHandler = (e: Event) => {
      const ce = e as CustomEvent<any>;
      const detail = ce && typeof ce === 'object' && 'detail' in ce ? ce.detail : {};
      if (!isAuthenticated) {
        if (detail?.intendedPath) {
          try { sessionStorage.setItem('intendedPath', detail.intendedPath); } catch {}
        }
        setAuthModalMode(detail?.mode === 'register' ? 'register' : 'login');
        setAuthModalOpen(true);
      }
    };
    window.addEventListener('open-auth-modal', openAuthHandler as EventListener);
    return () => window.removeEventListener('open-auth-modal', openAuthHandler as EventListener);
  }, [location, navigate, isAuthenticated]);

  // Handle deep-linking via /dashboard?open=feature for authenticated users
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(location.search);
    const open = params.get('open');
    if (!open) return;

    const routeMap: Record<string, string> = {
      consultation: '/consultation',
      documents: '/documents',
      'contract-analysis': '/contract-analysis',
      research: '/research',
      jurisprudence: '/jurisprudence',
      lawyer: '/lawyer',
    };
    const target = routeMap[open];
    if (target) {
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, location.search, navigate]);

  const loadRecent = async () => {
    setRecentLoading(true);
    setRecentError('');
    try {
      if (isAuthenticated) {
        // Authenticated: use protected endpoint
        try {
          const resp = await api.get('/documents/history?limit=5');
          const list = resp.data || [];
          if (Array.isArray(list) && list.length > 0) {
            setRecentDocs(list.slice(0, 3));
          } else {
            // Fallback to test endpoint even if empty
            const resp2 = await fetch('/api/v1/documents/test/history?limit=5');
            if (resp2.ok) {
              const data = await resp2.json();
              setRecentDocs((data || []).slice(0, 3));
            } else {
              setRecentDocs([]);
            }
          }
        } catch (e) {
          // Fallback to test endpoint
          const resp = await fetch('/api/v1/documents/test/history?limit=5');
          if (resp.ok) {
            const data = await resp.json();
            setRecentDocs((data || []).slice(0, 3));
          } else {
            throw new Error('Fallback failed');
          }
        }
      } else {
        // Unauthenticated: NEVER call axios (to avoid 401 redirect to /login)
        const resp = await fetch('/api/v1/documents/test/history?limit=5');
        if (resp.ok) {
          const data = await resp.json();
          setRecentDocs((data || []).slice(0, 3));
        } else {
          setRecentDocs([]);
        }
      }
    } catch (err) {
      setRecentError('Nepodařilo se načíst nedávné dokumenty');
    } finally {
      setRecentLoading(false);
    }
  };

  useEffect(() => {
    loadRecent();
    const handler = () => {
      loadRecent();
    };
    window.addEventListener('document-generated', handler);
    return () => window.removeEventListener('document-generated', handler);
  }, [isAuthenticated]);

  // Temporarily disabled authentication check
  // useEffect(() => {
  //   if (!isAuthenticated) {
  //     navigate('/');
  //   }
  // }, [isAuthenticated, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const features = [
    {
      icon: judgeIcon,
      title: 'Právní konzultace',
      description: 'Právní odpověď za 30 sekund. Ušetříte 2,500 Kč za hodinu advokáta.',
      path: '/consultation',
      primaryColor: '#e72f0d',
      secondaryColor: '#c72a0b',
      stats: { label: 'Odpověď za', value: '30 sekund' },
      chipLabel: 'Nejoblíbenější',
      chipColor: '#10b981'
    },
    {
      icon: documentIcon,
      title: 'Generování dokumentů',
      description: 'Profesionální dokumenty za 2 minuty. Úspora 4-6 hodin práce.',
      path: '/documents',
      primaryColor: '#e72f0d',
      secondaryColor: '#c72a0b',
      stats: { label: 'Typů dokumentů', value: '20+' },
      chipLabel: 'AI generování',
      chipColor: '#3b82f6'
    },
    {
      icon: documentIcon,
      title: 'Analýza smluv',
      description: 'Analyzujte smlouvy a identifikujte rizika automaticky',
      path: '/contract-analysis',
      primaryColor: '#e72f0d',
      secondaryColor: '#c72a0b',
      stats: { label: 'Přesnost', value: '95%' },
      chipLabel: 'Detekce rizik',
      chipColor: '#f59e0b'
    },
    {
      icon: shieldSearchIcon,
      title: 'Právní výzkum',
      description: 'Prohledávejte zákony a judikáty s AI vyhledáváním',
      path: '/research',
      primaryColor: '#e72f0d',
      secondaryColor: '#c72a0b',
      stats: { label: 'Zákonů', value: '1000+' },
      chipLabel: 'Kompletní databáze',
      chipColor: '#8b5cf6'
    }
  ];

  const recentActivity = [
    {
      type: 'consultation',
      title: 'Konzultace o nájemní smlouvě',
      time: 'Před 2 hodinami',
      icon: <Chat sx={{ fontSize: 20 }} />
    },
    {
      type: 'document',
      title: 'Vygenerována kupní smlouva',
      time: 'Včera',
      icon: <Description sx={{ fontSize: 20 }} />
    },
    {
      type: 'analysis',
      title: 'Analyzována pracovní smlouva',
      time: 'Před 3 dny',
      icon: <FindInPage sx={{ fontSize: 20 }} />
    }
  ];

  const userStats = [
    { label: 'Konzultací', value: '12', icon: <Chat /> },
    { label: 'Dokumentů', value: '8', icon: <Description /> },
    { label: 'Ušetřeno Kč', value: '8 500+', icon: <TrendingUp /> },
    { label: 'Ušetřeno hodin', value: '24+', icon: <AccessTime /> }
  ];

  // Temporarily disabled authentication check
  // if (!isAuthenticated) {
  //   return null;
  // }

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />

      <Box sx={{
        flex: 1,
        marginLeft: isMobile ? 0 : (collapsed ? '80px' : '280px'),
        paddingTop: isMobile ? '64px' : 0,
        minHeight: '100vh',
        backgroundColor: '#F2F2F2',
        position: 'relative',
        overflow: 'hidden',
        transition: 'margin-left 0.3s ease'
      }}>
        {/* Welcome Snackbar */}
        <Snackbar
          open={welcomeOpen}
          autoHideDuration={4000}
          onClose={() => setWelcomeOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setWelcomeOpen(false)} severity="success" role="alert" aria-live="polite" sx={{ width: '100%' }}>
            {`Vítejte, ${welcomeName || (user?.username || 'uživateli')}!`}
          </Alert>
        </Snackbar>
        {/* Gradient Background */}
        <Box sx={{
          position: 'absolute',
          left: '50%',
          top: '20%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '300%', sm: '200%', md: '1200px' },
          height: { xs: '300px', sm: '400px', md: '500px' },
          borderRadius: '50%',
          background: 'radial-gradient(circle at 50% 50%, rgba(100, 100, 255, 0.08) 0%, rgba(255, 100, 100, 0.04) 30%, rgba(128, 128, 128, 0.2) 60%, rgba(237, 237, 237, 0) 100%)',
          filter: 'blur(100px)',
          zIndex: 0,
          pointerEvents: 'none'
        }} />

        <Box sx={{
          position: 'relative',
          zIndex: 1,
          px: { xs: 3, sm: 4, md: 6 },
          py: { xs: 4, sm: 6 }
        }}>
          {/* Welcome Header */}
          <Box sx={{ mb: { xs: 6, sm: 8 }, textAlign: 'center' }}>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 800,
              fontSize: { xs: '36px', sm: '48px', md: '56px' },
              color: '#1F1F1F',
              mb: 2,
              letterSpacing: '-0.02em'
            }}>
              Váš Dashboard
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: { xs: '16px', sm: '18px', md: '20px' },
              color: '#666666',
              lineHeight: 1.6,
              maxWidth: '700px',
              mx: 'auto'
            }}>
              Sledujte svoje úspěchy a produktivitu
              {user?.username ? (
                <>
                  {', '}<strong>{user.username}</strong>
                </>
              ) : null}
            </Typography>
          </Box>

        {/* User Stats - HomePage style */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: { xs: 2, sm: 3 },
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          {userStats.map((stat, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                padding: { xs: '28px 20px', sm: '36px 24px' },
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,249,252,0.9) 100%)',
                backdropFilter: 'blur(16px)',
                borderRadius: '24px',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0 16px 48px rgba(0,0,0,0.12)',
                  border: '1px solid rgba(231, 47, 13, 0.2)'
                }
              }}
            >
              <Box sx={{ color: '#e72f0d', mb: 1, fontSize: '32px' }}>
                {stat.icon}
              </Box>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 800,
                fontSize: { xs: '28px', sm: '32px' },
                color: '#1F1F1F',
                letterSpacing: '-0.02em'
              }}>
                {stat.value}
              </Typography>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: { xs: '13px', sm: '14px' },
                color: '#666666',
                textAlign: 'center',
                lineHeight: 1.4
              }}>
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Main Features Grid */}
        <Typography sx={{
          fontFamily: 'Inter',
          fontWeight: 700,
          fontSize: { xs: '28px', sm: '32px', md: '38px' },
          color: '#1F1F1F',
          mb: 5,
          textAlign: 'center',
          letterSpacing: '-0.01em'
        }}>
          Vaše nástroje pro úsporu času a peněz
        </Typography>

        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3,
          mb: 8 
        }}>
          {features.map((feature, index) => (
            <Box key={index}>
              <Card
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => navigate(feature.path)}
                sx={{
                  p: 0,
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,249,252,0.95) 100%)',
                  backdropFilter: 'blur(16px)',
                  borderRadius: '24px',
                  boxShadow: hoveredCard === index
                    ? '0 20px 60px rgba(231, 47, 13, 0.25)'
                    : '0 8px 32px rgba(0,0,0,0.06)',
                  border: '1px solid',
                  borderColor: hoveredCard === index ? 'rgba(231, 47, 13, 0.4)' : 'rgba(255, 255, 255, 0.8)',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: hoveredCard === index ? 'translateY(-8px)' : 'translateY(0)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Icon Circle Background - Top Left */}
                <Box sx={{
                  position: 'absolute',
                  top: -40,
                  left: -40,
                  width: 160,
                  height: 160,
                  borderRadius: '50%',
                  background: hoveredCard === index
                    ? 'radial-gradient(circle, rgba(231, 47, 13, 0.2) 0%, transparent 70%)'
                    : 'radial-gradient(circle, rgba(231, 47, 13, 0.08) 0%, transparent 70%)',
                  transition: 'all 0.4s ease-out',
                  pointerEvents: 'none'
                }} />

                <Box sx={{ p: 4, position: 'relative', zIndex: 1 }}>
                  {/* Icon and Chip */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '16px',
                      background: hoveredCard === index
                        ? 'linear-gradient(135deg, rgba(231, 47, 13, 0.2) 0%, rgba(231, 47, 13, 0.15) 100%)'
                        : 'rgba(231, 47, 13, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.4s ease-out',
                      transform: hoveredCard === index ? 'scale(1.08)' : 'scale(1)',
                      boxShadow: hoveredCard === index ? '0 4px 16px rgba(231, 47, 13, 0.2)' : 'none'
                    }}>
                      <Box
                        component="img"
                        src={feature.icon}
                        alt={feature.title}
                        sx={{
                          width: 36,
                          height: 36,
                          transition: 'all 0.4s ease-out'
                        }}
                      />
                    </Box>
                    <Chip
                      label={feature.chipLabel}
                      size="small"
                      sx={{
                        backgroundColor: `${feature.chipColor}15`,
                        color: feature.chipColor,
                        fontWeight: 600,
                        fontSize: '11px',
                        border: `1px solid ${feature.chipColor}30`,
                        transition: 'all 0.4s ease-out',
                        opacity: hoveredCard === index ? 1 : 0.8
                      }}
                    />
                  </Box>

                  {/* Title */}
                  <Typography sx={{
                    fontFamily: 'Inter',
                    fontWeight: 700,
                    fontSize: '24px',
                    color: '#1F1F1F',
                    mb: 1.5,
                    letterSpacing: '-0.01em'
                  }}>
                    {feature.title}
                  </Typography>

                  {/* Description */}
                  <Typography sx={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: '15px',
                    color: '#666666',
                    lineHeight: 1.6,
                    mb: 4
                  }}>
                    {feature.description}
                  </Typography>

                  {/* Stats and Arrow */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pt: 3,
                    borderTop: hoveredCard === index ? '1px solid rgba(231, 47, 13, 0.2)' : '1px solid rgba(0,0,0,0.06)',
                    transition: 'border-color 0.4s ease-out'
                  }}>
                    <Box>
                      <Typography sx={{
                        fontFamily: 'Inter',
                        fontWeight: 800,
                        fontSize: '28px',
                        color: '#e72f0d',
                        letterSpacing: '-0.02em',
                        lineHeight: 1,
                        mb: 0.5
                      }}>
                        {feature.stats.value}
                      </Typography>
                      <Typography sx={{
                        fontFamily: 'Inter',
                        fontWeight: 500,
                        fontSize: '13px',
                        color: '#999999'
                      }}>
                        {feature.stats.label}
                      </Typography>
                    </Box>

                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      background: hoveredCard === index
                        ? 'linear-gradient(135deg, rgba(231, 47, 13, 0.2) 0%, rgba(231, 47, 13, 0.15) 100%)'
                        : 'rgba(231, 47, 13, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.4s ease-out',
                      boxShadow: hoveredCard === index ? '0 4px 12px rgba(231, 47, 13, 0.2)' : 'none'
                    }}>
                      <ArrowForward sx={{
                        fontSize: 24,
                        color: '#e72f0d',
                        transform: hoveredCard === index ? 'translateX(4px)' : 'none',
                        transition: 'all 0.4s ease-out'
                      }} />
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Box>
          ))}
        </Box>

        {/* Recent Activity */}
        <Box sx={{ mb: 8 }}>
          <Typography sx={{
            fontFamily: 'Inter',
            fontWeight: 700,
            fontSize: { xs: '28px', sm: '32px', md: '38px' },
            color: '#1F1F1F',
            mb: 5,
            textAlign: 'center',
            letterSpacing: '-0.01em'
          }}>
            Nedávná aktivita
          </Typography>

          {recentLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} sx={{ p: 3, borderRadius: '20px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="50%" />
                      <Skeleton variant="text" width="30%" />
                    </Box>
                    <Skeleton variant="circular" width={24} height={24} />
                  </Box>
                </Card>
              ))}
            </Box>
          ) : recentError ? (
            <Alert severity="error">{recentError}</Alert>
          ) : recentDocs.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
              Zatím žádné vygenerované dokumenty.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {recentDocs.map((doc, index) => (
                <Card
                  key={doc.filename + index}
                  onClick={() => window.open(doc.download_url, '_blank')}
                  sx={{
                    p: 3,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,249,252,0.95) 100%)',
                    backdropFilter: 'blur(16px)',
                    borderRadius: '20px',
                    border: '1px solid rgba(255, 255, 255, 0.8)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateX(12px)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                      borderColor: 'rgba(231, 47, 13, 0.2)'
                    }
                  }}
                >
                  <Box sx={{
                    p: 2,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(231, 47, 13, 0.1) 0%, rgba(231, 47, 13, 0.05) 100%)',
                    color: '#e72f0d'
                  }}>
                    <Description sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      fontSize: '16px',
                      color: '#1F1F1F',
                      mb: 0.5
                    }}>
                      {`Vygenerováno: ${doc.contract_type.toUpperCase()}`}
                    </Typography>
                    <Typography sx={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: '12px',
                      color: '#666666'
                    }}>
                      {new Date(doc.generated_at).toLocaleString('cs-CZ')}
                    </Typography>
                  </Box>
                  <ArrowForward sx={{ color: '#CCCCCC' }} />
                </Card>
              ))}
            </Box>
          )}
        </Box>

        {/* Document History */}
        <Box sx={{ mb: 8 }}>
          <Typography sx={{
            fontFamily: 'Inter',
            fontWeight: 700,
            fontSize: { xs: '28px', sm: '32px', md: '38px' },
            color: '#1F1F1F',
            mb: 5,
            textAlign: 'center',
            letterSpacing: '-0.01em'
          }}>
            Historie vygenerovaných dokumentů
          </Typography>

          <DocumentHistory />
        </Box>

        {/* Quick Actions */}
        <Box sx={{
          p: { xs: '40px 28px', sm: '48px 40px' },
          mb: 8,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,249,252,0.95) 100%)',
          backdropFilter: 'blur(16px)',
          borderRadius: '24px',
          border: '1px solid rgba(255, 255, 255, 0.8)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.06)'
        }}>
          <Typography sx={{
            fontFamily: 'Inter',
            fontWeight: 700,
            fontSize: { xs: '28px', sm: '32px', md: '38px' },
            color: '#1F1F1F',
            mb: 4,
            textAlign: 'center',
            letterSpacing: '-0.01em'
          }}>
            Rychlé akce
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              onClick={() => navigate('/consultation')}
              sx={{
                px: 5,
                py: 2,
                bgcolor: '#10b981 !important',
                backgroundColor: '#10b981 !important',
                background: '#10b981 !important',
                color: '#FFFFFF !important',
                fontWeight: 600,
                fontSize: '16px',
                textTransform: 'none',
                border: 'none !important',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: '#059669 !important',
                  backgroundColor: '#059669 !important',
                  background: '#059669 !important',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(16, 185, 129, 0.4)'
                }
              }}
            >
              Získat právní konzultaci →
            </Button>
          </Box>
        </Box>
        </Box>
      </Box>
      {/* Auth Modal in register mode */}
      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode={authModalMode}
      />
    </Box>
  );
};

export default DashboardPage;
